import type { RouteIdentifier } from "@/services/logger/logger-types";
import {
  LogCategories,
  LogSubCategories,
  LogTags,
} from "@/services/logger/logger-types";

const ScreenRouteNames = {
  ROOT: {
    code: "",
    path: "/",
    tag: LogTags.LOGIN_CODE,
    category: LogCategories.LOGIN_GROUP_ID,
    subCategory: LogSubCategories.NONE,
  },
  GROUP: {
    code: "",
    path: "group",
    tag: LogTags.LOGIN_CODE,
    category: LogCategories.LOGIN_GROUP_ID,
    subCategory: LogSubCategories.NONE,
  },
  DISCLAIMER: {
    code: "",
    path: "disclaimer",
    tag: LogTags.ACCOUNT_ACCESS,
    category: LogCategories.NONE,
    subCategory: LogSubCategories.NONE,
  },
  LOCATE: {
    code: "",
    path: "locate",
    tag: LogTags.NONE,
    category: LogCategories.LOCATE_ACCOUNT,
    subCategory: LogSubCategories.NONE,
  },
  LOGIN: {
    code: "",
    path: "login",
    tag: LogTags.ACCOUNT_ACCESS,
    category: LogCategories.LOGIN,
    subCategory: LogSubCategories.NONE,
  },
  GUEST_LOGIN: {
    code: "",
    path: "guest-login",
    tag: LogTags.ACCOUNT_ACCESS,
    category: LogCategories.NONE,
    subCategory: LogSubCategories.NONE,
  },
  REGISTER: {
    code: "",
    path: "register",
    tag: LogTags.ACCOUNT_ACCESS,
    category: LogCategories.SIGN_UP,
    subCategory: LogSubCategories.NONE,
  },
  ACCOUNT_RESET: {
    code: "",
    path: "account-reset",
    tag: LogTags.ACCOUNT_ACCESS,
    category: LogCategories.PASSWORD_RESET,
    subCategory: LogSubCategories.FORGOT_PASSWORD,
  },
  VERIFICATION: {
    code: "",
    path: "verification",
    tag: LogTags.ACCOUNT_ACCESS,
    category: LogCategories.PASSWORD_RESET,
    subCategory: LogSubCategories.NONE,
  },
  VERIFY_OTP: {
    code: "",
    path: "verify-otp",
    tag: LogTags.ACCOUNT_ACCESS,
    category: LogCategories.NONE,
    subCategory: LogSubCategories.NONE,
  },
  CARE_HISTORY: {
    code: "MH001",
    path: "care-history",
    legacyName: "Care & Claims History",
    tag: LogTags.MODULE_ACCESS,
    category: LogCategories.CARE_AND_CLAIMS_HISTORY,
    subCategory: LogSubCategories.NONE,
  },
  ID_CARD: {
    code: "BI004",
    path: "card",
    legacyName: "ID Card",
    tag: LogTags.MODULE_ACCESS,
    category: LogCategories.ID_CARD,
    subCategory: LogSubCategories.NONE,
  },

  // TODO: JUST HIGH LEVEL LOGS MAINTAINED
  HOME: {
    code: "",
    path: "home",
    tag: LogTags.MODULE_ACCESS,
    category: LogCategories.HOME,
    subCategory: LogSubCategories.NONE,
  },
  MY_BENEFIT: {
    code: "BI001",
    path: "benefits",
    legacyName: "My Benefits",
    tag: LogTags.MODULE_ACCESS,
    category: LogCategories.MY_BENEFITS,
    subCategory: LogSubCategories.NONE,
  },
  NOTIFICATION: {
    code: "N001",
    path: "notifications",
    legacyName: "Notifications",
    tag: LogTags.MODULE_ACCESS,
    category: LogCategories.NOTIFICATIONS,
    subCategory: LogSubCategories.NONE,
  },
  SPEND_SUMMARY: {
    code: "SS001",
    path: "spend-summary",
    legacyName: "Spend Summary",
    tag: LogTags.MODULE_ACCESS,
    category: LogCategories.SPEND_SUMMARY,
    subCategory: LogSubCategories.NONE,
  },
  SERVICE: {
    code: "BI005",
    path: "services",
    legacyName: "Services",
    tag: LogTags.MODULE_ACCESS,
    category: LogCategories.SERVICES,
    subCategory: LogSubCategories.NONE,
  },
  BALANCE_BILL: {
    code: "BLB001",
    path: "balance-bill",
    tag: LogTags.MODULE_ACCESS,
    category: LogCategories.BALANCE_BILL,
    subCategory: LogSubCategories.NONE,
  },
  COST_ESTIMATOR: {
    code: "BI009",
    path: "cost-estimator",
    legacyName: "Cost Estimator",
    tag: LogTags.MODULE_ACCESS,
    category: LogCategories.COST_ESTIMATOR,
    subCategory: LogSubCategories.NONE,
  },
  HCBB_COST_ESTIMATOR: {
    code: "BI011",
    path: "cost-estimator-enhanced",
    legacyName: "Cost Estimator Enhanced",
    tag: LogTags.MODULE_ACCESS,
    category: LogCategories.HCBB_COST_ESTIMATOR,
    subCategory: LogSubCategories.NONE,
  },
  HCBB_COST_ESTIMATOR_RESULT: {
    code: "BI012",
    path: "results",
    legacyName: "Cost Estimator Enhanced Results",
    tag: LogTags.MODULE_ACCESS,
    category: LogCategories.HCBB_COST_ESTIMATOR,
    subCategory: LogSubCategories.NONE,
  },
  // Both detail screens sit under the results path, so the route guard —
  // which reads only the first path segment — gates them with the
  // estimator itself. Their `code` is empty on purpose: codes are
  // backend-owned, and inventing one risks colliding with a code the
  // feature list later assigns to something else.
  HCBB_FACILITY_DETAIL: {
    code: "",
    path: "facility",
    legacyName: "Facility Details",
    tag: LogTags.MODULE_ACCESS,
    category: LogCategories.HCBB_COST_ESTIMATOR,
    subCategory: LogSubCategories.NONE,
  },
  HCBB_PROVIDER_DETAIL: {
    code: "",
    path: "provider",
    legacyName: "Provider Details",
    tag: LogTags.MODULE_ACCESS,
    category: LogCategories.HCBB_COST_ESTIMATOR,
    subCategory: LogSubCategories.NONE,
  },
  CSR_CHAT: {
    code: "",
    path: "csr-chat",
    legacyName: "CSR Chat",
    tag: LogTags.MODULE_ACCESS,
    category: LogCategories.CSR_CHAT,
    subCategory: LogSubCategories.NONE,
  },
  PROVIDER_SEARCH_BY_GROUP: {
    code: "PSS002",
    path: "provider-search-by-group",
    legacyName: "Provider Search",
    tag: LogTags.MODULE_ACCESS,
    category: LogCategories.PROVIDER_SEARCH,
    subCategory: LogSubCategories.NONE,
  },

  // TODO: NO LOGS AT ALL
  PROVIDER_INFO: {
    code: "",
    path: "provider-info",
    tag: LogTags.MODULE_ACCESS,
  },
  PROVIDER_SEARCH: {
    code: "PSS001",
    path: "provider-search",
    tag: LogTags.MODULE_ACCESS,
  },
  PROVIDER_SEARCH_RESULT: {
    code: "PSR001",
    path: "results",
    tag: LogTags.MODULE_ACCESS,
  },
  PROVIDER_SEARCH_PT: {
    code: "PSPT001",
    path: "provider-search-pt",
    legacyName: "Cost Estimator",
    tag: LogTags.MODULE_ACCESS,
  },
  PROFILE: { code: "MPF001", path: "profile", tag: LogTags.NONE }, // Not Used in Web
  ERROR: { code: "", path: "error", tag: LogTags.MODULE_ACCESS },
  BIOMETRICS: { code: "BI006", path: "biometrics", tag: LogTags.MODULE_ACCESS },
  MY_PCP: { code: "BI007", path: "my-pcp", tag: LogTags.MODULE_ACCESS },
  MY_ACCOUNT: { code: "BI008", path: "my-account", tag: LogTags.MODULE_ACCESS },
  OLD_COST_ESTIMATOR: {
    code: "BI009",
    path: "provider-search-pt",
    legacyName: "Cost Estimator",
    tag: LogTags.MODULE_ACCESS,
  },
  COST_ESTIMATOR_RESULT: {
    code: "BI010",
    path: "results",
    tag: LogTags.MODULE_ACCESS,
  },
  MFA_LOGIN: { code: "", path: "mfa-login", tag: LogTags.MODULE_ACCESS },
  PLAN_ENROLLMENT: {
    code: "OEN001",
    path: "open-enrollment",
    legacyName: "Plan Enrollment",
    tag: LogTags.MODULE_ACCESS,
  },
  CHAT: {
    code: "C001",
    path: "chat",
    legacyName: "Member Support Chatbot",
    tag: LogTags.MODULE_ACCESS,
  },
  CHATBOT: { code: "CBDQ001", path: "", tag: LogTags.MODULE_ACCESS },
  COVID: { code: "N005", path: "", tag: LogTags.MODULE_ACCESS },
  SURVEY: { code: "SRY001", path: "survey", tag: LogTags.MODULE_ACCESS },
  PRVOIDER_SEARCH_PT_RESULT: {
    code: "PSRPT001",
    path: "",
    tag: LogTags.MODULE_ACCESS,
  },
  VIDEOS: {
    code: "",
    path: "videos-screen",
    legacyName: "Videos",
    tag: LogTags.MODULE_ACCESS,
    category: LogCategories.VIDEOS,
    subCategory: LogSubCategories.NONE,
  },
  EBIX: { code: "", path: "ebix", tag: LogTags.MODULE_ACCESS },

  ACCESS_DENIED: { code: "", path: "access-denied" },
  SESSION_EXPIRED: { code: "", path: "session-expired" },
} satisfies Record<string, RouteIdentifier>;

export default ScreenRouteNames;

export const getRouteNameForCode = (code: string): string | undefined => {
  const entry = Object.values(ScreenRouteNames).find(
    (item) => item.code === code,
  );
  return entry?.path || undefined;
};

export const getLegacyNameForPath = (path: string): string | undefined => {
  const entry = Object.values(ScreenRouteNames).find(
    (item) => item.path === path,
  );
  if (entry && "legacyName" in entry) {
    return (entry as { legacyName?: string }).legacyName;
  }
  return undefined;
};

export const Guest_Module_Mapping = {
  home: [],
  "provider-search": [
    ScreenRouteNames.PROVIDER_SEARCH.path,
    ScreenRouteNames.PROVIDER_SEARCH_RESULT.path,
  ],
  "cost-estimator": [
    ScreenRouteNames.COST_ESTIMATOR.path,
    ScreenRouteNames.COST_ESTIMATOR_RESULT.path,
  ],
};

export const getAllowedRoutesForKey = (routeKey: string): string[] => {
  return (
    Guest_Module_Mapping[routeKey as keyof typeof Guest_Module_Mapping] || []
  );
};

// Maps GlobalFlag boolean keys → the route paths they unlock.
// Routes listed here are NOT present in featureList.byPlan — they are
// group-level features controlled solely by the GlobalFlag API.
// The route guard checks this map as a second access gate alongside byPlan.
import type { GlobalFlag } from "@/model/global-flag";

export const globalFlagRoutes: Partial<Record<keyof GlobalFlag, string[]>> = {
  showBilling: [ScreenRouteNames.BALANCE_BILL.path],
  showBiometric: [ScreenRouteNames.BIOMETRICS.path],
  showPlanEnrollment: [ScreenRouteNames.PLAN_ENROLLMENT.path],
  showChat: [ScreenRouteNames.CSR_CHAT.path],
  showVideos: [ScreenRouteNames.VIDEOS.path],
};
